import { useIntl } from "react-intl";
import type { ApiOperationItems } from "~enums/apiOperationItems";
import { ApiOperations } from "~enums/apiOperations";
import { useApiOperationNotifications } from "~hooks/useApiOperationNotifications.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { Response } from "~types/response.ts";

type OperationOptions<TResponse> = {
  successMessageId?: string;
  isSucceeded?: (response: TResponse) => boolean;
};

type ExecuteOptions<TResponse> = OperationOptions<TResponse> & {
  operation?: ApiOperations;
};

const getOperationPayload = (response: unknown) => {
  const isResponseEnvelope = Boolean(response) && typeof response === "object" && Array.isArray((response as Response<unknown>).data);

  return isResponseEnvelope ? (response as Response<unknown>).data[0] : response;
};

// Operations either confirm themselves with a success flag or answer with the affected entity
const isOperationConfirmed = (response: unknown) => {
  const payload = getOperationPayload(response);

  if (payload && typeof payload === "object" && "success" in payload) {
    return payload.success === true;
  }

  return Boolean(payload);
};

export const useApiOperations = (item: ApiOperationItems) => {
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { notifySuccess, notifyFailure } = useApiOperationNotifications(item);

  const execute = async <TResponse>(
    request: () => Promise<TResponse>,
    { successMessageId, operation, isSucceeded = isOperationConfirmed }: ExecuteOptions<TResponse> = {},
  ) => {
    try {
      const response = await request();

      // A 2xx response that does not confirm the operation is not reported by the useApi error handlers
      if (!isSucceeded(response)) {
        notifyFailure(operation);
        return undefined;
      }

      if (successMessageId) {
        notification.success({ message: formatMessage({ id: successMessageId }) });
      } else if (operation) {
        notifySuccess(operation);
      }

      return response;
    } catch {
      // Failed requests are reported by the useApi error handlers
      return undefined;
    }
  };

  const create = <TResponse>(request: () => Promise<TResponse>, options?: OperationOptions<TResponse>) =>
    execute(request, { ...options, operation: ApiOperations.CREATE });

  const update = <TResponse>(request: () => Promise<TResponse>, options?: OperationOptions<TResponse>) =>
    execute(request, { ...options, operation: ApiOperations.UPDATE });

  const remove = <TResponse>(request: () => Promise<TResponse>, options?: OperationOptions<TResponse>) =>
    execute(request, { ...options, operation: ApiOperations.DELETE });

  return { execute, create, update, remove, notifySuccess };
};
